import React, { useCallback, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import FlatListItemCard from 'design-system/FlatListItemCard';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import useColorScheme from 'hooks/useColorScheme';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectBudgetingPosition } from 'features/analytics/selectors';
import { useOpenCategory } from 'features/analytics/hooks/useOpenCategory';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import { Spacer, TextButtonLink } from 'design-system';
import SectionHeader from 'design-system/SectionHeader';
import { isRolloverActive, selectBudgetedSubCategoriesForParentCategory, selectMonthlyTotalsWithBudgets, selectUnbudgetCategoriesForParentCategory, } from '../selectors';
import BudgetingCategoryRow from './BudgetingCategoryRow';
import { isWeb } from '../../../constants';
import UnbudgetedSpendRow from './UnbudgetedSpendRow';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => String(item.id);
const SubCategoriesList = ({ parentCategory, }) => {
    const subcategories = useAppSelector((store) => selectBudgetedSubCategoriesForParentCategory(store, parentCategory.id));
    const position = useAppSelector(selectBudgetingPosition);
    const unbudgetedData = useAppSelector((store) => selectUnbudgetCategoriesForParentCategory(store, parentCategory.id));
    const data = useMemo(() => [...subcategories, ...unbudgetedData], [subcategories, unbudgetedData]);
    const styles = useStyles(styleSet);
    const filteredTotals = useAppSelector(selectMonthlyTotalsWithBudgets);
    const openCategory = useOpenCategory(filteredTotals, position, 'MonthlyCategory', undefined, undefined, 'push');
    const renderItem = useCallback(({ item, index }) => (<FlatListItemCard index={index} itemsLength={data.length} style={styles.listItem}>
        {!item.budgetLimit ? (<UnbudgetedSpendRow hideTransactionsCount category={item} onPress={() => openCategory({ data: item, type: 'category' })}/>) : (<BudgetingCategoryRow category={item} onPress={() => openCategory({ data: item, type: 'category' })}/>)}
      </FlatListItemCard>), [data, openCategory, styles.listItem]);
    const navigation = useMainStackNavigation();
    const ListHeaderComponent = useMemo(() => (<SectionHeader horizontalMargins largeVerticalMargins button={<TextButtonLink text="Edit" onPress={() => navigation.navigateDeprecated('Budgets', {
                scrollToParentCategoryId: parentCategory.id,
            })}/>}>
        Subcategories
      </SectionHeader>), [parentCategory.id]);
    const isRollover = useAppSelector(isRolloverActive);
    const ListFooterComponent = useMemo(() => {
        if (!subcategories.length || !isRollover)
            return null;
        return (<>
        <Spacer.H16 />
        <InfoCard>
          <Text TextThin-14 Secondary>
            Subcategory budgets do not roll over. They will remain at their set
            amount, even if the parent category budget changes.
          </Text>
        </InfoCard>
      </>);
    }, [isRollover, subcategories.length]);
    const colorScheme = useColorScheme();
    const baseProps = {
        scrollEnabled: false,
        data,
        renderItem,
        keyExtractor,
        ListHeaderComponent,
        ListFooterComponent,
    };
    if (!data.length)
        return null;
    if (isWeb) {
        return <Animated.FlatList {...baseProps}/>;
    }
    return <AnimatedFlashList {...baseProps} extraData={colorScheme}/>;
};
const styleSet = createStyleSheets((colors) => ({
    listItem: {
        backgroundColor: colors.cards.onDark,
    },
    addIcon: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(44),
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: rem(16),
    },
    addButton: {
        marginHorizontal: rem(16),
    },
    addContainer: {
        marginTop: rem(8),
    },
}));
export default SubCategoriesList;
